/**
 * Client-side Build rail completeness from questDetail.
 * Green (done) vs orange miss when required content is empty/missing.
 * Does not invent API readiness — computes from fields already on the quest.
 */

import { UTCFormatDateShort } from '../../utils/date'
import { isWelcomeEmailComplete } from './enrollmentValidation'
import { isParticipantPaired } from './utils/guidePairingUtils'

const hasText = (v) => typeof v === 'string' && v.trim().length > 0

const guidePairingStats = (q) => {
  if (q?.guidePairingStats) return q.guidePairingStats
  const list = q?.participants || q?.questParticipants || q?.enrolledUsers
  if (!Array.isArray(list)) return { total: 0, pairedCount: 0 }
  return {
    total: list.length,
    pairedCount: list.filter(isParticipantPaired).length,
  }
}
// Shown in the rail (dot) but excluded from the stage count / issue totals.
// TODO: Count Wisdom Guide Pairing separately in the future, then remove this.
const UNCOUNTED_ITEMS = ['prep-guide-pairing']

const hasNum = (v) =>
  v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v))

const itemChecks = {
  'setup-name': (q) =>
    hasText(q?.name) && hasText(q?.title) && hasText(q?.description),
  'setup-dates': (q) => {
    const list = q?.programDatesList
    if (!Array.isArray(list) || list.length === 0) return false
    return !!(list[0]?.startDate && list[0]?.endDate)
  },
  'setup-details': (q) =>
    hasText(q?.duration) || hasNum(q?.groupSize) || hasText(q?.location),
  'setup-price': (q) =>
    hasNum(q?.investments?.standardRate) ||
    hasNum(q?.investments?.singleSupplement),
  'setup-cover': (q) => hasText(q?.imageFileName) || hasText(q?.questImage),
  'content-hero': (q) =>
    Array.isArray(q?.headerImage) &&
    q.headerImage.some((img) => img && img.fileName),
  'content-inclusions': (q) => {
    const exp = q?.inclusions?.experiential || q?.experientialInclusions || []
    const dig = q?.inclusions?.digital || q?.digitalInclusions || []
    const flat = q?.inclusions
    const hasInclusion = (i) =>
      hasText(i?.inclusion || i?.text || (typeof i === 'string' ? i : ''))
    if (Array.isArray(flat) && flat.some(hasInclusion)) return true
    return (
      (Array.isArray(exp) && exp.some(hasInclusion)) ||
      (Array.isArray(dig) && dig.some(hasInclusion))
    )
  },
  'content-faculty': (q) =>
    Array.isArray(q?.faculties) && q.faculties.some((f) => hasText(f?.name)),
  'content-journey': (q) =>
    Array.isArray(q?.journeySections) &&
    q.journeySections.some((j) => hasText(j?.title) || hasText(j?.description)),
  'content-faqs': (q) =>
    Array.isArray(q?.faqs) &&
    q.faqs.some((f) => hasText(f?.question) || hasText(f?.answer)),
  'content-interest': (q) => {
    const qs =
      q?.expressInterestForm?.questions ||
      q?.inquiryCustomForm?.questions ||
      q?.inquiryQuestions ||
      []
    return Array.isArray(qs) && qs.length > 0
  },
  'enrol-signup': (q) =>
    hasText(q?.welcomeLetter) ||
    hasText(q?.onboarding?.welcomeLetter) ||
    hasText(q?.onboardingLetter) ||
    hasText(q?.signupWelcomeLetter) ||
    hasText(q?.onboarding?.letter) ||
    hasText(q?.signupPageContent) ||
    hasText(q?.signupHeader) ||
    hasText(q?.signupBody) ||
    hasText(q?.signupTitle) ||
    hasText(q?.onboarding?.signupHeader),
  'enrol-email': (q) => {
    const html =
      q?.welcomeEmailHtml ||
      q?.emailTemplate ||
      q?.welcomeEmail ||
      q?.welcomeEmailBody ||
      ''
    if (html) return isWelcomeEmailComplete(html)
    // Subject-only is not enough — body with {{ button }} is required.
    return false
  },
  'enrol-form': (q) => {
    const questions =
      q?.enrollmentQuestions ||
      q?.customForm?.questions ||
      q?.customFormQuestions ||
      []
    const count =
      q?.enrollmentQuestionCount ||
      (Array.isArray(questions) ? questions.length : 0)
    return count > 0
  },
  'enrol-process': (q) => {
    const cfg =
      q?.stepConfiguration ||
      q?.enrollmentProcess ||
      q?.steps ||
      q?.enrollmentSteps
    if (!cfg || typeof cfg !== 'object') return false
    return !!(cfg.step1 || cfg.step2 || cfg.step3 || cfg.step4)
  },
  'prep-learning': () => true, // optional — never a publish / rail miss
  'prep-schedule': (q) => {
    const days = q?.itinerary || q?.itineraryDays || []
    if (!Array.isArray(days) || days.length === 0) return false
    // Every dated day needs at least one filled entry — empty days
    // (0 badge / no subtitle details) keep the rail on "needs attention".
    return days.every(
      (d) =>
        Array.isArray(d?.itineraries) &&
        d.itineraries.some(
          (e) => hasText(e?.itinerary) || hasText(e?.title) || e?.fromTime,
        ),
    )
  },
  'prep-checklist': (q) => {
    const raw = q?.travelChecklist || q?.checklist || []
    const sections = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.sections)
      ? raw.sections
      : []
    if (!Array.isArray(sections) || sections.length === 0) return false
    return sections.some(
      (s) =>
        Array.isArray(s?.items) &&
        s.items.some((i) => hasText(i?.item || i?.text || i)),
    )
  },
  'prep-orientation': (q) =>
    hasText(q?.orientationCall?.link) &&
    !!(q?.orientationCall?.date || q?.orientationCall?.callDate),
  'prep-guide-pairing': (q) => {
    const { total, pairedCount } = guidePairingStats(q)
    // Red dot while any participant is unpaired, green once all are paired.
    return pairedCount >= total
  },
}

const countLearning = (q) => {
  const listen =
    q?.journeyAudios?.length ||
    q?.audios?.length ||
    q?.learningResources?.listen?.length ||
    0
  const watch =
    q?.videos?.length ||
    q?.learningResources?.watch?.length ||
    q?.resources?.videos?.length ||
    0
  const read =
    q?.blogs?.length ||
    q?.learningResources?.read?.length ||
    q?.resources?.blogs?.length ||
    0
  return { listen, watch, read, total: listen + watch + read }
}

const checklistStats = (q) => {
  const raw = q?.travelChecklist || q?.checklist || []
  const sections = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.sections)
    ? raw.sections
    : []
  let items = 0
  sections.forEach((s) => {
    if (Array.isArray(s?.items)) {
      items += s.items.filter((i) => hasText(i?.item || i?.text || i)).length
    }
  })
  return { sections: sections.length, items }
}

const noteFor = (key, q) => {
  if (key === 'content-hero') {
    const n = (q?.headerImage || []).filter((i) => i?.fileName).length
    return n ? `${n} images` : null
  }
  if (key === 'content-inclusions') {
    const hasInclusion = (i) =>
      hasText(i?.inclusion || i?.text || (typeof i === 'string' ? i : ''))
    const exp = (
      q?.experientialInclusions ||
      q?.inclusions?.experiential ||
      []
    ).filter(hasInclusion).length
    const dig = (q?.digitalInclusions || q?.inclusions?.digital || []).filter(
      hasInclusion,
    ).length
    const n = exp + dig
    return n ? `${n} inclusions` : null
  }
  if (key === 'content-faculty') {
    const n = (q?.faculties || []).length
    return n ? `${n} faculty` : null
  }
  if (key === 'content-journey') {
    const n = (q?.journeySections || []).length
    return n ? `${n} phases` : null
  }
  if (key === 'content-faqs') {
    const n = (q?.faqs || []).length
    return n ? `${n} questions` : null
  }
  if (key === 'content-interest') {
    const qs =
      q?.expressInterestForm?.questions ||
      q?.inquiryCustomForm?.questions ||
      q?.inquiryQuestions ||
      []
    if (Array.isArray(qs) && qs.length > 0) return 'Enabled'
    return null
  }
  if (key === 'content-partners') {
    const n = (q?.partners || []).length
    return n ? `${n} partners` : null
  }
  if (key === 'content-testimonials') {
    const sections = (q?.testimonials || []).filter(
      (s) =>
        hasText(s?.title) || (s?.videos || []).some((v) => hasText(v?.title)),
    )
    const n = sections.length
    return n ? `${n} of 3` : null
  }
  if (key === 'enrol-form') {
    const questions =
      q?.enrollmentQuestions ||
      q?.customForm?.questions ||
      q?.customFormQuestions ||
      []
    const n =
      q?.enrollmentQuestionCount ||
      (Array.isArray(questions) ? questions.length : 0)
    return n ? `${n} qs` : null
  }
  if (key === 'prep-learning') {
    const { total } = countLearning(q)
    return total ? String(total) : null
  }
  if (key === 'prep-schedule') {
    const days = q?.itinerary || q?.itineraryDays || []
    const n = Array.isArray(days) ? days.length : 0
    if (!n) return 'Not set'
    return `${n} day${n === 1 ? '' : 's'}`
  }
  if (key === 'prep-checklist') {
    const { sections, items } = checklistStats(q)
    if (!sections && !items) return null
    return `${sections} / ${items}`
  }
  if (key === 'prep-orientation') {
    if (hasText(q?.orientationCall?.link) && q?.orientationCall?.date) {
      return null
    }
    return 'Not set'
  }
  if (key === 'prep-guide-pairing') {
    const { total, pairedCount } = guidePairingStats(q)
    if (total === 0) return null
    return `${pairedCount} / ${total} paired`
  }
  return null
}

/**
 * Accordion / workspace header meta string for a build item.
 */
export const getBuildItemMeta = (key, q) => {
  if (!q) return null
  if (key === 'content-hero') return noteFor(key, q)
  if (key === 'content-inclusions') return noteFor(key, q)
  if (key === 'content-faculty') return noteFor(key, q)
  if (key === 'content-journey') {
    const n = (q?.journeySections || []).length
    return n ? `${n} programme phases` : null
  }
  if (key === 'content-faqs') return noteFor(key, q)
  if (key === 'content-interest') return noteFor(key, q) || null
  if (key === 'content-partners') return noteFor(key, q)
  if (key === 'content-testimonials') return noteFor(key, q)
  if (key === 'prep-learning') {
    const { listen, watch, read, total } = countLearning(q)
    if (!total) return null
    return `${listen} listen · ${watch} watch · ${read} read`
  }
  if (key === 'prep-schedule') {
    const days = q?.itinerary || q?.itineraryDays || []
    const n = Array.isArray(days) ? days.length : 0
    return n ? `${n} days` : 'Not scheduled'
  }
  if (key === 'prep-checklist') {
    const { sections, items } = checklistStats(q)
    if (!sections && !items) return null
    return `${sections} sections · ${items} items`
  }
  if (key === 'prep-orientation') {
    const date = q?.orientationCall?.date || q?.orientationCall?.callDate
    if (date) {
      return UTCFormatDateShort(date) || 'Scheduled'
    }
    return 'Not scheduled'
  }
  if (key === 'prep-guide-pairing') return noteFor(key, q)
  if (key === 'enrol-form') return noteFor(key, q)
  return null
}

/** Publish blockers — required setup + content + enrol items that fail checks. */
export const getPublishBlockers = (questDetail) => {
  if (!questDetail) return ['Experience details']
  const required = [
    'setup-name',
    'setup-dates',
    'setup-price',
    'setup-cover',
    'content-hero',
    'content-inclusions',
    'content-faculty',
    'content-journey',
    'content-faqs',
    'content-interest',
    'enrol-signup',
    'enrol-email',
    'enrol-form',
  ]
  const labels = {
    'setup-name': 'Name, public title and summary',
    'setup-dates': 'Programme dates',
    'setup-price': 'Shared room rate',
    'setup-cover': 'Header image',
    'content-hero': 'Slider images',
    'content-inclusions': 'Inclusions',
    'content-faculty': 'Faculty',
    'content-journey': 'Journey phases',
    'content-faqs': 'FAQs',
    'content-interest': 'Express Interest questions',
    'enrol-signup': 'Signup page copy',
    'enrol-email': 'Welcome email',
    'enrol-form': 'Enrollment form questions',
  }
  return required
    .filter((key) => !(itemChecks[key] ? itemChecks[key](questDetail) : true))
    .map((key) => labels[key] || key)
}

// Rail dot per item, from the item's own fields:
//   no field filled -> 'empty' (hollow), some -> 'partial' (red), all -> 'done'.
// Repeatable items (list of entries) need every filled entry to be complete.
const entryFields = (list, isFilled, isComplete) => {
  const filled = (Array.isArray(list) ? list : []).filter(isFilled)
  return filled.length ? filled.map(isComplete) : [false]
}

const itemFields = {
  'setup-name': (q) => [
    hasText(q?.name),
    hasText(q?.title),
    hasText(q?.programType),
    hasText(q?.description),
  ],
  'setup-dates': (q) => {
    const d = q?.programDatesList?.[0]
    return [!!d?.startDate, !!d?.endDate]
  },
  'setup-details': (q) => [
    hasText(q?.duration),
    hasNum(q?.groupSize),
    hasText(q?.location),
  ],
  'setup-price': (q) => [
    hasNum(q?.investments?.standardRate),
    hasNum(q?.investments?.singleSupplement),
  ],
  'setup-cover': (q) => [hasText(q?.imageFileName) || hasText(q?.questImage)],
  'content-hero': (q) => [itemChecks['content-hero'](q)],
  'content-inclusions': (q) => {
    const hasInclusion = (i) =>
      hasText(i?.inclusion || i?.text || (typeof i === 'string' ? i : ''))
    const exp = q?.experientialInclusions || q?.inclusions?.experiential || []
    const dig = q?.digitalInclusions || q?.inclusions?.digital || []
    return [
      Array.isArray(exp) && exp.some(hasInclusion),
      Array.isArray(dig) && dig.some(hasInclusion),
    ]
  },
  'content-faculty': (q) =>
    entryFields(
      q?.faculties,
      (f) => hasText(f?.name),
      (f) => hasText(f?.name),
    ),
  'content-journey': (q) =>
    entryFields(
      q?.journeySections,
      (j) => hasText(j?.title) || hasText(j?.description),
      (j) => hasText(j?.title) && hasText(j?.description),
    ),
  'content-faqs': (q) =>
    entryFields(
      q?.faqs,
      (f) => hasText(f?.question) || hasText(f?.answer),
      (f) => hasText(f?.question) && hasText(f?.answer),
    ),
  'content-interest': (q) => [itemChecks['content-interest'](q)],
  'content-rsvp': (q) => {
    const r = q?.learnMore?.rsvp || {}
    if (hasText(r.recordingLink)) return [true]
    if (r.date || r.time || hasText(r.link)) {
      return [!!r.date, !!r.time, hasText(r.link)]
    }
    return [false]
  },
  'content-partners': (q) =>
    entryFields(
      q?.partners,
      (p) => hasText(p?.title) || hasText(p?.name) || hasText(p?.logoFileName),
      (p) =>
        (hasText(p?.title) || hasText(p?.name)) && hasText(p?.logoFileName),
    ),
  'content-video': (q) => [
    hasText(q?.mainVideo?.title),
    hasText(q?.mainVideo?.youtubeLink),
    hasText(q?.mainVideo?.posterImageFileName),
  ],
  'content-testimonials': (q) =>
    entryFields(
      q?.testimonials,
      (t) =>
        hasText(t?.title) || (t?.videos || []).some((v) => hasText(v?.title)),
      (t) =>
        hasText(t?.title) && (t?.videos || []).some((v) => hasText(v?.title)),
    ),
  'enrol-signup': (q) => [itemChecks['enrol-signup'](q)],
  'enrol-email': (q) => {
    const html =
      q?.welcomeEmailHtml ||
      q?.emailTemplate ||
      q?.welcomeEmail ||
      q?.welcomeEmailBody ||
      ''
    return [hasText(html), itemChecks['enrol-email'](q)]
  },
  'enrol-form': (q) => [itemChecks['enrol-form'](q)],
  'enrol-process': (q) => [itemChecks['enrol-process'](q)],
  'prep-learning': (q) => [countLearning(q).total > 0],
  'prep-schedule': (q) => {
    const days = q?.itinerary || q?.itineraryDays || []
    const entryOk = (e) =>
      hasText(e?.itinerary) || hasText(e?.title) || e?.fromTime
    const dayHas = (d) =>
      Array.isArray(d?.itineraries) && d.itineraries.some(entryOk)
    if (!Array.isArray(days) || days.length === 0) return [false]
    return days.map(dayHas)
  },
  'prep-checklist': (q) => {
    const raw = q?.travelChecklist || q?.checklist || []
    const sections = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.sections)
      ? raw.sections
      : []
    const hasItem = (sec) =>
      Array.isArray(sec?.items) &&
      sec.items.some((i) => hasText(i?.item || i?.text || i))
    return entryFields(
      sections,
      (sec) => hasText(sec?.title) || hasItem(sec),
      (sec) => hasText(sec?.title) && hasItem(sec),
    )
  },
  'prep-orientation': (q) => [
    hasText(q?.orientationCall?.link),
    !!(q?.orientationCall?.date || q?.orientationCall?.callDate),
  ],
}

const itemDotState = (key, q) => {
  const fields = itemFields[key] ? itemFields[key](q) : null
  if (!fields) return null
  const filled = fields.filter(Boolean).length
  if (filled === 0) return 'empty'
  return filled === fields.length ? 'done' : 'partial'
}

/**
 * @returns {Object} stageMeta keyed by stage key for ExperienceBuildRail
 */
export const getBuildStageMeta = (
  questDetail,
  { isNewQuest, isNewlyCopied, railExtrasReady = true } = {},
) => {
  if (isNewQuest || !questDetail) {
    return {
      setup: {
        done: false,
        countLabel: '0 of 5',
        issueCount: 5,
        itemIssues: {
          'setup-name': true,
          'setup-dates': true,
          'setup-details': true,
          'setup-price': true,
          'setup-cover': true,
        },
        itemNotes: {},
      },
      sell: {
        done: false,
        countLabel: '0 of 6',
        issueCount: 6,
        itemIssues: {},
        itemNotes: {},
      },
      enrol: {
        done: false,
        countLabel: '0 of 4',
        issueCount: 4,
        itemIssues: {},
        itemNotes: {},
      },
      travel: {
        done: false,
        countLabel: 'Later',
        itemIssues: {},
        itemNotes: {},
      },
    }
  }

  if (isNewlyCopied) {
    return {
      setup: {
        done: false,
        countLabel: 'Review',
        issueCount: 1,
        itemIssues: {
          'setup-name': true,
          'setup-dates': true,
        },
        itemNotes: {},
      },
      sell: {
        done: true,
        countLabel: 'Copied',
        issueCount: 0,
        itemIssues: {},
        itemNotes: {},
      },
      enrol: {
        done: true,
        countLabel: 'Copied',
        issueCount: 0,
        itemIssues: {},
        itemNotes: {},
      },
      travel: {
        done: true,
        countLabel: 'Copied',
        issueCount: 0,
        itemIssues: {},
        itemNotes: {},
      },
    }
  }

  const stages = {
    setup: [
      'setup-name',
      'setup-dates',
      'setup-details',
      'setup-price',
      'setup-cover',
    ],
    sell: [
      'content-hero',
      'content-inclusions',
      'content-faculty',
      'content-journey',
      'content-faqs',
      'content-interest',
    ],
    enrol: ['enrol-signup', 'enrol-email', 'enrol-form', 'enrol-process'],
    travel: [
      'prep-learning',
      'prep-schedule',
      'prep-checklist',
      'prep-orientation',
      'prep-guide-pairing',
    ],
  }

  // Hold all stage labels until async rail APIs finish — avoids flashing
  // partial counts (e.g. Set up "5 of 5" while other stages still show "…",
  // or Participant prep "2 issues" → "1 issue").
  if (!railExtrasReady) {
    const pending = {}
    Object.keys(stages).forEach((stageKey) => {
      pending[stageKey] = {
        pending: true,
        done: false,
        countLabel: '…',
        issueCount: null,
        itemIssues: {},
        itemNotes: {},
      }
    })
    return pending
  }

  const meta = {}
  Object.keys(stages).forEach((stageKey) => {
    const keys = stages[stageKey]
    const itemIssues = {}
    const itemNotes = {}
    let complete = 0
    let total = 0
    keys.forEach((key) => {
      const ok = itemChecks[key] ? itemChecks[key](questDetail) : true
      if (!ok) itemIssues[key] = true
      // Dot state is kept above; counting is skipped for uncounted items.
      if (!UNCOUNTED_ITEMS.includes(key)) {
        total += 1
        if (ok) complete += 1
      }
      const note = noteFor(key, questDetail)
      if (note) itemNotes[key] = note
    })
    const issues = total - complete
    const done = issues === 0
    let countLabel = `${complete} of ${total}`
    if (stageKey === 'travel' && !done && complete === 0) {
      countLabel = 'Later'
    } else if (stageKey === 'travel' && issues > 0) {
      countLabel = `${issues} issue${issues === 1 ? '' : 's'}`
    }
    const itemState = {}
    const stateKeys =
      stageKey === 'sell'
        ? [
            ...keys,
            'content-rsvp',
            'content-partners',
            'content-video',
            'content-testimonials',
          ]
        : keys
    stateKeys.forEach((key) => {
      const state = itemDotState(key, questDetail)
      if (state) itemState[key] = state
    })
    meta[stageKey] = {
      pending: false,
      itemState,
      done,
      countLabel,
      issueCount: done ? 0 : issues,
      itemIssues,
      itemNotes,
    }
  })
  return meta
}
